import {
  AlertTriangle,
  BarChart3,
  Database,
  LayoutDashboard,
  Clock,
  MapPin,
  Menu,
  Moon,
  Plus,
  ShieldCheck,
  Settings,
  Sun,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BrandMark, BrandWordmark } from "../shared/BrandIdentity";

export type AppView =
  | "dashboard"
  | "desk"
  | "trips"
  | "cash"
  | "parties"
  | "analytics";
export type AppTheme = "dark" | "light";

type AppShellProps = {
  activeView: AppView;
  onViewChange: (view: AppView) => void;
  theme: AppTheme;
  onThemeChange: (theme: AppTheme) => void;
  onNewTrip: () => void;
  onOpenDataSafety: () => void;
  isDataBusy: boolean;
  dataMessage?: DataMessage;
  children: ReactNode;
};

export type DataMessage = {
  tone: "info" | "success" | "error";
  text: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  durationMs?: number;
};

const navItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "desk", label: "Desk", icon: AlertTriangle },
  { id: "trips", label: "Trips", icon: MapPin },
  { id: "cash", label: "Cash", icon: Clock },
  { id: "parties", label: "Parties", icon: Database },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
] as const;

export function AppShell({
  activeView,
  onViewChange,
  theme,
  onThemeChange,
  onNewTrip,
  onOpenDataSafety,
  isDataBusy,
  dataMessage,
  children,
}: AppShellProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<DataMessage | undefined>();
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const navigationMenuRef = useRef<HTMLDivElement>(null);
  const toastClass =
    toastMessage?.tone === "error"
      ? "orbit-tone-rose"
      : toastMessage?.tone === "success"
        ? "orbit-tone-emerald"
        : "orbit-tone-blue";

  useEffect(() => {
    if (!dataMessage || dataMessage.text === "Saved on this device") return;
    setToastMessage(dataMessage);
    const timeoutId = window.setTimeout(
      () => setToastMessage(undefined),
      dataMessage.durationMs ?? (dataMessage.action ? 10_000 : 3_600)
    );
    return () => window.clearTimeout(timeoutId);
  }, [dataMessage]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activeView]);

  useEffect(() => {
    document.body.dataset.haulledgerTheme = theme;
    return () => {
      delete document.body.dataset.haulledgerTheme;
    };
  }, [theme]);

  useEffect(() => {
    if (!isSettingsOpen && !isNavigationOpen) return;
    const closeMenus = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!settingsMenuRef.current?.contains(target)) setIsSettingsOpen(false);
      if (!navigationMenuRef.current?.contains(target)) setIsNavigationOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSettingsOpen(false);
        setIsNavigationOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeMenus);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeMenus);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isNavigationOpen, isSettingsOpen]);

  const changeView = (view: AppView) => {
    onViewChange(view);
    setIsSettingsOpen(false);
    setIsNavigationOpen(false);
  };

  return (
    <div className="orbit-app min-h-screen" data-theme={theme}>
      <header className="orbit-glass orbit-safe-top sticky top-0 z-40 px-3 py-2.5 sm:px-4 sm:py-3">
        <div className="relative mx-auto flex max-w-[1500px] items-center justify-between gap-3 max-[359px]:gap-2">
          <div className="flex min-w-0 items-center gap-3 max-[359px]:gap-2">
            <BrandMark className="h-12 w-12 shrink-0 max-[359px]:h-10 max-[359px]:w-10" />
            <div className="min-w-0">
              <p className="truncate leading-tight">
                <BrandWordmark className="text-lg max-[359px]:text-base" />
              </p>
              <p className="truncate text-[11px] font-semibold leading-tight text-slate-500 max-[359px]:hidden">
                Operations and money desk
              </p>
            </div>
          </div>

          <nav
            aria-label="Primary navigation"
            className="orbit-primary-nav absolute left-1/2 hidden -translate-x-1/2 items-center xl:flex"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-label={item.label}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => changeView(item.id)}
                  className={`orbit-primary-nav-item ${isActive ? "is-active" : ""}`}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 max-[359px]:gap-1">
            <div ref={navigationMenuRef} className="relative hidden lg:block xl:hidden">
              <button
                type="button"
                onClick={() => setIsNavigationOpen((value) => !value)}
                aria-label="Navigation menu"
                aria-expanded={isNavigationOpen}
                className="orbit-raised inline-flex h-10 w-10 items-center justify-center text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                <Menu size={18} />
              </button>
              {isNavigationOpen ? (
                <div
                  role="menu"
                  aria-label="Navigation menu"
                  className="orbit-card absolute right-0 top-full z-50 mt-2 w-56 p-2"
                >
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeView === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="menuitem"
                        onClick={() => changeView(item.id)}
                        className={`flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold ${
                          isActive ? "orbit-tab-active" : "orbit-tab"
                        }`}
                      >
                        <Icon size={16} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <div ref={settingsMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIsSettingsOpen((value) => !value)}
                disabled={isDataBusy}
                aria-label="Settings menu"
                aria-expanded={isSettingsOpen}
                className="orbit-raised inline-flex h-10 w-10 items-center justify-center text-slate-600 transition hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <Settings size={17} />
              </button>
              {isSettingsOpen ? (
                <div className="orbit-card absolute right-0 top-full z-50 mt-2 w-64 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      onThemeChange(theme === "dark" ? "light" : "dark");
                      setIsSettingsOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <span className="inline-flex items-center gap-2">
                      {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                      {theme === "dark" ? "Light theme" : "Dark theme"}
                    </span>
                    <span className="text-xs text-slate-500">
                      {theme === "dark" ? "Off" : "On"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onOpenDataSafety();
                      setIsSettingsOpen(false);
                    }}
                    className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <ShieldCheck size={16} />
                    Data Safety
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onNewTrip}
              disabled={isDataBusy}
              aria-label="New trip"
              className="orbit-button-primary px-3 py-2 text-sm max-[359px]:h-10 max-[359px]:w-10 max-[359px]:px-0 sm:px-4"
            >
              <Plus size={17} />
              <span className="hidden sm:inline">New Trip</span>
              <span className="sm:hidden max-[359px]:hidden">New</span>
            </button>
          </div>
        </div>
      </header>
      <main className="orbit-mobile-main mx-auto max-w-[1500px] px-3 py-4 sm:px-4 sm:py-5 lg:px-6">
        {children}
      </main>
      <nav
        aria-label="Mobile navigation"
        className="orbit-mobile-nav orbit-glass fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 lg:hidden"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          return (
            <button
              key={item.id}
              type="button"
              aria-label={item.label}
              title={item.label}
              onClick={() => {
                changeView(item.id);
              }}
              className={`orbit-mobile-nav-item ${isActive ? "is-active" : ""}`}
            >
              <Icon size={20} />
              <span className="sr-only">{item.label}</span>
            </button>
          );
        })}
      </nav>
      {toastMessage ? (
        <div
          role="status"
          className={`orbit-toast fixed right-3 z-[60] flex max-w-[min(360px,calc(100vw-1.5rem))] items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold shadow-2xl backdrop-blur-md sm:right-4 ${toastClass}`}
        >
          <span className="min-w-0 flex-1">{toastMessage.text}</span>
          {toastMessage.action ? (
            <button
              type="button"
              onClick={() => {
                setToastMessage(undefined);
                toastMessage.action?.onClick();
              }}
              className="shrink-0 rounded-lg border border-current px-3 py-1.5 text-xs font-bold"
            >
              {toastMessage.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
